import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { SDF_ISO_THRESHOLD } from '~/engine/util/algorithms/sdf/sdfConstants';

/**
 * A structure's signed-distance field as the CPU reads it: one slice per
 * Z, indexed `[z][y * sizeX + x]`, with `grid` the field's OWN grid rather
 * than the image's — a contour texture is cropped to its content and may
 * be rescaled or rotated against the image it belongs to.
 *
 * This, not the DICOM contour sequence, is what a structure IS here.
 * Contours exist only for structures that arrived as DICOM or have been
 * written back out, so anything the user has just made carries none, and
 * logic that reads them finds an empty structure and answers as though it
 * were absent.
 */
export interface StructureSdf {
    readonly pixels: readonly Uint8Array[];
    readonly grid: Grid;
}

/**
 * The engine's rasterised field for a structure, as much of it as reading
 * one needs. Structural rather than the engine type so the domain does not
 * depend on the engine to describe what it reads.
 */
interface RasterisedStructureTexture {
    readonly pixels: readonly Uint8Array[];
    readonly textureGrid: Grid;
}

/**
 * An engine texture as a {@link StructureSdf}, or null when nothing has
 * rasterised one — which is simply a structure with no field to read,
 * never an error.
 */
export function structureSdfOf(
    texture: RasterisedStructureTexture | null | undefined
): StructureSdf | null {
    return texture
        ? { pixels: texture.pixels, grid: texture.textureGrid }
        : null;
}

/** The field's voxel containing `patientPoint`, or null when the point
 *  falls outside the field's own extent. Floor, not round: a voxel index
 *  addresses the cell starting at it, matching the view's slice snap. */
function voxelAt(
    sdf: StructureSdf,
    patientPoint: ReadonlyVec3
): { x: number; y: number; z: number } | null {
    const v = sdf.grid.toVoxelSpace(patientPoint as vec3);
    const x = Math.floor(v[0]);
    const y = Math.floor(v[1]);
    const z = Math.floor(v[2]);
    if (
        x < 0 ||
        y < 0 ||
        z < 0 ||
        x >= sdf.grid.sizeX ||
        y >= sdf.grid.sizeY ||
        z >= sdf.grid.sizeZ ||
        z >= sdf.pixels.length
    ) {
        return null;
    }
    return { x, y, z };
}

/** Whether the structure encloses `patientPoint`. */
export function isInsideAt(
    sdf: StructureSdf,
    patientPoint: ReadonlyVec3
): boolean {
    const at = voxelAt(sdf, patientPoint);
    if (!at) {
        return false;
    }
    const slice = sdf.pixels[at.z];
    return (
        slice !== undefined &&
        slice[at.y * sdf.grid.sizeX + at.x] >= SDF_ISO_THRESHOLD
    );
}

/**
 * The area the structure encloses on the slice through `patientPoint`, in
 * mm². Returns 0 when the point falls outside the field.
 *
 * In mm² and not in voxels because each field carries its OWN grid: a
 * texture is cropped to its content and need not share another
 * structure's spacing, so a voxel count is not comparable between two
 * structures and there is no common factor to cancel.
 */
export function enclosedAreaMm2OnSliceAt(
    sdf: StructureSdf,
    patientPoint: ReadonlyVec3
): number {
    const at = voxelAt(sdf, patientPoint);
    if (!at) {
        return 0;
    }
    const slice = sdf.pixels[at.z];
    if (!slice) {
        return 0;
    }
    let inside = 0;
    for (let i = 0; i < slice.length; i++) {
        if (slice[i] >= SDF_ISO_THRESHOLD) {
            inside++;
        }
    }
    return inside * sdf.grid.resX * sdf.grid.resY;
}

/** Total volume the structure encloses, in mm³. */
export function volumeMm3(sdf: StructureSdf): number {
    let inside = 0;
    for (const slice of sdf.pixels) {
        if (!slice) {
            continue;
        }
        for (let i = 0; i < slice.length; i++) {
            if (slice[i] >= SDF_ISO_THRESHOLD) {
                inside++;
            }
        }
    }
    return inside * sdf.grid.resX * sdf.grid.resY * sdf.grid.resZ;
}
